import { createSlice, PayloadAction } from '@reduxjs/toolkit'

/** Single item stored in the cart. */
export type CartItem = {
  id: string
  name: string
  price: string
  quantity: number
  size?: string
  /** When true, this item does not incur postage at checkout. */
  freeShipping?: boolean
}

/** Cart slice state shape. */
type CartState = {
  items: CartItem[]
}

const initialState: CartState = {
  items: [],
}

/** Payload shape for adding items to the cart. */
type AddToCartPayload = {
  item: Omit<CartItem, 'quantity'>
  quantity: number
}

/** Cart slice for managing selected merchandise. */
const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addItem: (state, action: PayloadAction<AddToCartPayload>) => {
      const { item, quantity } = action.payload
      const existing = state.items.find(
        (entry) => entry.id === item.id && entry.size === item.size,
      )
      if (existing) {
        existing.quantity += quantity
      } else {
        state.items.push({ ...item, quantity })
      }
    },
    removeItem: (state, action: PayloadAction<{ id: string; size?: string }>) => {
      const { id, size } = action.payload
      state.items = state.items.filter((item) => !(item.id === id && item.size === size))
    },
    updateQuantity: (
      state,
      action: PayloadAction<{ id: string; size?: string; quantity: number }>,
    ) => {
      const { id, size, quantity } = action.payload
      const existing = state.items.find((item) => item.id === id && item.size === size)
      if (!existing) return
      if (quantity <= 0) {
        state.items = state.items.filter((item) => !(item.id === id && item.size === size))
      } else {
        existing.quantity = quantity
      }
    },
    clearCart: (state) => {
      state.items = []
    },
  },
})

export const { addItem, removeItem, updateQuantity, clearCart } = cartSlice.actions
export default cartSlice.reducer
